import { useModal } from './modal-container'
import {
  useOrder,
  useEvent,
  useEmailOrderReceipt,
  useOrderReviewsByOrder,
  useRefundOrderItems,
  useRefundOrderPartial,
} from '@/lib/api/queries'
import { useEffect, useState } from 'react'
import { formatDate } from '@/lib/date'
import type { User, OrderItem, OrderReview } from '@/lib/api/types'
import { useAnyPermission, usePermission } from '@/lib/permissions'
import { ChevronDown, Printer, Mail, Star, ThumbsUp, ThumbsDown, RotateCcw, X, Tag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { printerService } from '@/lib/printer'
import { buildOrderReceiptData, buildOrderReceiptHtml } from '@/lib/printer/receipt-builders'
import { EmailReceiptModal } from '@/components/modals/email-receipt-modal'
import { PrinterSelectionModal } from '@/features/printer/components/PrinterSelectionModal'
import { formatTokenNumber } from '@/features/promotions/money'
import { getPrinterSettings } from '@/lib/printer/settings'
import { OrderCardPayment } from './order-card-payment'
import { openReceiptPreview, printHtmlReceipt } from '@/lib/printer/system-print'

// Backend permission split (verified against orders/views.py + orders/permissions.py):
//   - refund_partial  → HasEventPermission(['can_refund_order'])
//   - refund_items    → IsVendorsOrder, which accepts any of these write perms
const REFUND_ITEMS_PERMS = ['can_edit_orders', 'can_create_order', 'change_order']

interface LineDiscount {
  /** Unit list price at order time, in menu units. */
  listPrice: number
  /** Unit price charged, in menu units. */
  chargedPrice: number
  /** Menu units taken off the line: (list − charged) × qty. */
  amount: number
}

/**
 * What an order line was discounted by: its list price at order time above
 * the price it charged. Null for a line that charged its list price, and for
 * orders placed before list prices were recorded.
 */
function lineDiscountOf(item: OrderItem): LineDiscount | null {
  const chargedPrice = item.price_at_ordertime
  // A DecimalField: the API sends it as a string ("12.0000").
  const listPrice = Number(item.list_price_at_ordertime ?? NaN)
  if (chargedPrice == null || !Number.isFinite(listPrice) || listPrice <= chargedPrice) return null
  return { listPrice, chargedPrice, amount: (listPrice - chargedPrice) * (item.qty || 1) }
}

/**
 * The line statuses an order's total counts (`TOTAL_ORDER_STATUSES` in the
 * backend's orders/models.py): a refunded line's discount is no longer part
 * of what the order charged. A line without a status counts.
 */
const TOTAL_LINE_STATUSES = new Set(['ordered', 'accepted', 'completed', 'delivered'])
const countsInTotal = (item: OrderItem) => !item.status || TOTAL_LINE_STATUSES.has(item.status)

/**
 * Token amounts on a discounted line and the Discount total. Whole numbers
 * (every token event) print as the rows' Math.round does; a fractional
 * amount keeps up to two decimals, so rounding never hides a discount.
 */
const formatDiscountTokens = (value: number) => formatTokenNumber(value, false)

/**
 * The list price struck through after a discounted line's charged unit price,
 * and a pill naming the discount: the schedule (staff get its name, anyone
 * else "Promotion") or the item's own sale.
 */
function LineDiscountTag({ item, discount }: { item: OrderItem; discount: LineDiscount }) {
  return (
    <>
      <s className="text-neutral-400 dark:text-neutral-500" title="List price">
        {formatDiscountTokens(discount.listPrice)}
      </s>
      {item.discount_source === 'schedule' ? (
        <span
          title={item.discount_name ? `Discount schedule: ${item.discount_name}` : 'Discount schedule'}
          className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] leading-none font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/25 dark:text-emerald-300"
        >
          <Tag className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="max-w-[10rem] truncate">{item.discount_name || 'Promotion'}</span>
        </span>
      ) : item.discount_source === 'item_sale' ? (
        <span
          title="Item sale price"
          className="rounded-full px-1.5 py-0.5 text-[11px] leading-none font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
        >
          Sale
        </span>
      ) : null}
    </>
  )
}

export function OrderModal() {
  const { modalState, closeAllModals, openUserModal } = useModal()
  const canSeeUserInfo = usePermission('can_see_user_info')
  // Two distinct backend permissions gate the two refund modes — we mirror
  // that here so a user with only one perm doesn't see the wrong default
  // mode (and 403 on submit) or get the button hidden entirely.
  const canRefundOrder = usePermission('can_refund_order')
  const canRefundItems = useAnyPermission(REFUND_ITEMS_PERMS)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showPrinterModal, setShowPrinterModal] = useState(false)
  const [printError, setPrintError] = useState<string | null>(null)
  const [printerState, setPrinterState] = useState(printerService.getState())

  // Refund form state
  const [showRefundForm, setShowRefundForm] = useState(false)
  const [refundMode, setRefundMode] = useState<'items' | 'amount'>('items')
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set())
  const [customAmount, setCustomAmount] = useState('')

  const orderId = modalState.orderModal?.orderId
  const eventId = modalState.orderModal?.eventId

  const { data: order, isLoading, error } = useOrder(orderId || 0)
  const { data: event } = useEvent(eventId || 0)
  const { data: orderReviewsData } = useOrderReviewsByOrder(orderId || 0)
  const emailReceiptMutation = useEmailOrderReceipt()
  const refundItemsMutation = useRefundOrderItems()
  const refundPartialMutation = useRefundOrderPartial()

  const orderReview = orderReviewsData?.results?.[0] as OrderReview | undefined

  // Get token value for dollar conversion (default 2.0)
  const tokenValue = event?.token_value || 2.0

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAllModals()
    }
    if (modalState.orderModal) {
      document.addEventListener('keydown', handleEscape)
      document.body.classList.add('overflow-hidden')
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.classList.remove('overflow-hidden')
    }
  }, [modalState.orderModal, closeAllModals])

  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(false)
    if (dropdownOpen) {
      document.addEventListener('click', handleClickOutside)
    }
    return () => document.removeEventListener('click', handleClickOutside)
  }, [dropdownOpen])

  useEffect(() => {
    const unsubscribe = printerService.subscribe(setPrinterState)
    return unsubscribe
  }, [])

  if (!modalState.orderModal) return null

  const handleUserClick = (userId: number) => {
    if (eventId) {
      closeAllModals()
      setTimeout(() => openUserModal(userId, eventId), 100)
    }
  }

  const formatTokensWithDollars = (tokens: number) => {
    const dollars = tokens * tokenValue
    return `${Number(tokens).toFixed(1)} Tokens ($${dollars.toFixed(2)})`
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-500'
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-500'
      case 'delivered':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-500'
      case 'refunded':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-500'
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-500'
    }
  }

  // Get vendor info - vendor_event can be a number or object
  const vendorEvent = order?.vendor_event
  const vendorEventObj = typeof vendorEvent === 'object' ? vendorEvent : null
  const vendorName = vendorEventObj?.vendor?.name || 'Unknown Vendor'
  const boothName = vendorEventObj?.booth || vendorName

  // Get user info - user can be a number or object
  const orderUser = order?.user
  const orderUserObj = typeof orderUser === 'object' ? orderUser as User : null

  // Calculate total quantity
  const totalQuantity = order?.order_items?.reduce((sum: number, item: OrderItem) => sum + (item.qty || item.quantity || 0), 0) || 0
  // Menu units the discounted lines took off their list prices, over the
  // lines the order's Total counts.
  const discountTokens = order?.order_items?.reduce(
    (sum, item) => sum + (countsInTotal(item) ? lineDiscountOf(item)?.amount ?? 0 : 0), 0) ?? 0
  const printerSettings = getPrinterSettings()

  const handlePrintReceipt = async () => {
    if (!order) return
    setPrintError(null)
    const settings = getPrinterSettings()
    if (settings.preferredConnection === 'system') {
      try {
        const html = buildOrderReceiptHtml(order, event || undefined)
        if (settings.showPreviewBeforePrinting) {
          openReceiptPreview(html)
        } else {
          printHtmlReceipt(html)
        }
      } catch (error) {
        setPrintError(error instanceof Error ? error.message : 'Failed to open system print dialog')
      }
      return
    }

    const receiptData = buildOrderReceiptData(order, event || undefined)
    const success = await printerService.printReceipt(receiptData)
    if (!success) {
      const printerError = printerService.getState().error || 'Failed to print receipt'
      setPrintError(printerError)
    }
  }

  const handleEmailReceipt = async () => {
    if (!order || !eventId) return
    await emailReceiptMutation.mutateAsync({ eventId, orderId: order.id })
  }

  // ---- Refund flow ----
  // Refund affordance shows if the user can use EITHER mode. The mode
  // toggle below hides whichever modes the user can't actually use.
  const isRefundable = !!order &&
    (canRefundOrder || canRefundItems) &&
    order.status !== 'refunded' &&
    order.status !== 'cancelled' &&
    !!order.order_items?.length

  // Sum of selected items' totals (in tokens). React Compiler handles memoization.
  const selectedItemsTokens = order?.order_items
    ? order.order_items
        .filter((it) => selectedItemIds.has(it.id))
        .reduce((sum, it) => sum + (it.total || 0), 0)
    : 0

  const parsedCustomAmount = parseFloat(customAmount) || 0
  const orderTotal = order?.total || 0

  const refundIsValid = refundMode === 'items'
    ? selectedItemIds.size > 0
    : parsedCustomAmount > 0 && parsedCustomAmount <= orderTotal

  const refundIsPending = refundItemsMutation.isPending || refundPartialMutation.isPending
  const refundError = refundItemsMutation.error || refundPartialMutation.error

  const refundTotalTokens = refundMode === 'items' ? selectedItemsTokens : parsedCustomAmount

  const openRefundForm = () => {
    setShowRefundForm(true)
    // Default to whichever mode the user can actually use. If both, prefer
    // items mode (the historical default and the cleaner full-line UX). If
    // they only have partial-refund permission, open straight into amount
    // mode so they're not staring at a disabled items picker.
    setRefundMode(canRefundItems ? 'items' : 'amount')
    setSelectedItemIds(new Set())
    setCustomAmount('')
    refundItemsMutation.reset()
    refundPartialMutation.reset()
  }

  const closeRefundForm = () => {
    setShowRefundForm(false)
    setSelectedItemIds(new Set())
    setCustomAmount('')
    refundItemsMutation.reset()
    refundPartialMutation.reset()
  }

  const toggleItemSelection = (itemId: number) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  const selectAllItems = () => {
    if (!order?.order_items) return
    setSelectedItemIds(new Set(order.order_items.map((it) => it.id)))
  }

  const handleRefundSubmit = async () => {
    if (!order || !eventId || !refundIsValid) return
    try {
      if (refundMode === 'items') {
        await refundItemsMutation.mutateAsync({
          orderId: order.id,
          eventId,
          itemIds: Array.from(selectedItemIds),
        })
      } else {
        await refundPartialMutation.mutateAsync({
          orderId: order.id,
          eventId,
          amount: parsedCustomAmount,
        })
      }
      closeRefundForm()
    } catch {
      // error surfaces via refundError below
    }
  }

  return (
    <div className="fixed inset-0 z-110 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={closeAllModals}
      />

      {/* Modal content */}
      <div
        className="w-full max-w-6xl max-h-[90vh] bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 p-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              Order #{order?.order_display_id || orderId}
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              {vendorName} - {boothName}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!!order && (
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setDropdownOpen(!dropdownOpen)
                  }}
                  className="inline-flex items-center px-2 py-1 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 text-xs font-medium rounded-md transition-colors"
                  type="button"
                >
                  <ChevronDown className={cn('h-3 w-3 transition-transform', dropdownOpen && 'rotate-180')} />
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setDropdownOpen(false)
                          void handlePrintReceipt()
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-900 flex items-center whitespace-nowrap"
                      >
                        <Printer className="h-4 w-4 mr-2 shrink-0" />
                        Print Receipt
                      </button>
                      <button
                        onClick={() => {
                          setDropdownOpen(false)
                          setShowEmailModal(true)
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-900 flex items-center whitespace-nowrap"
                      >
                        <Mail className="h-4 w-4 mr-2 shrink-0" />
                        Email Receipt
                      </button>
                      {isRefundable && (
                        <button
                          onClick={() => {
                            setDropdownOpen(false)
                            openRefundForm()
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-orange-700 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 flex items-center whitespace-nowrap border-t border-neutral-100 dark:border-neutral-700"
                        >
                          <RotateCcw className="h-4 w-4 mr-2 shrink-0" />
                          Refund Order
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200"
              onClick={closeAllModals}
              aria-label="Close modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Content area with sidebar layout */}
        <div className="flex flex-col md:flex-row max-h-[calc(90vh-6rem)] overflow-hidden">
          {/* Sidebar navigation */}
          <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950">
            <nav className="p-2 md:p-4 md:sticky">
              <ul className="space-y-2">
                <li>
                  <div className="w-full flex items-center p-3 text-left rounded-lg border-l-4 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 border-blue-600 dark:border-blue-500">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-3">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    <span className="font-medium">Order Details</span>
                  </div>
                </li>
              </ul>
            </nav>
          </div>

          {/* Main content area */}
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </div>
            ) : error ? (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-700 dark:text-red-400">Failed to load order: {error.message}</p>
              </div>
            ) : order ? (
              <div className="space-y-6">
                {printError && (
                  <div className="flex items-center justify-between gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                    <span>{printError}</span>
                    {printerSettings.preferredConnection !== 'system' && printerState.status !== 'connected' && (
                      <button
                        type="button"
                        onClick={() => setShowPrinterModal(true)}
                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Setup Printer
                      </button>
                    )}
                  </div>
                )}
                {/* Order Overview and Attendee Info - Side by Side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Order Overview Card */}
                  <div className="bg-white dark:bg-neutral-900 p-4 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                    <h3 className="flex items-center text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      Order Overview
                    </h3>
                    <div className="flex-1 space-y-4">
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Order #:</span>
                        <span className="text-neutral-900 dark:text-neutral-100 text-sm">{order.order_display_id || order.id}</span>
                      </div>
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Transaction Date:</span>
                        <span className="text-neutral-900 dark:text-neutral-100 text-sm">{formatDate(order.created_at || order.created, 'MMM d, yyyy h:mm a')}</span>
                      </div>
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Amount:</span>
                        <span className="text-neutral-900 dark:text-neutral-100 text-sm">{formatTokensWithDollars(order.total || 0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Attendee Information Card */}
                  <div className="bg-white dark:bg-neutral-900 p-4 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                    <h3 className="flex items-center text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      Attendee Info
                    </h3>
                    <div className="flex-1 space-y-4">
                      {orderUserObj ? (
                        canSeeUserInfo ? (
                          <>
                            <div className="grid grid-cols-[140px_1fr] gap-2">
                              <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Name:</span>
                              <button
                                onClick={() => handleUserClick(orderUserObj.id)}
                                className="text-blue-600 dark:text-blue-500 hover:underline text-left text-sm font-semibold"
                              >
                                {orderUserObj.first_name} {orderUserObj.last_name}
                              </button>
                            </div>
                            <div className="grid grid-cols-[140px_1fr] gap-2">
                              <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Phone:</span>
                              <span className="text-neutral-900 dark:text-neutral-100 text-sm">{orderUserObj.phone_number || orderUserObj.phone || '-'}</span>
                            </div>
                            <div className="grid grid-cols-[140px_1fr] gap-2">
                              <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Email:</span>
                              <span className="text-neutral-900 dark:text-neutral-100 text-sm">{orderUserObj.email || '-'}</span>
                            </div>
                          </>
                        ) : (
                          <div className="grid grid-cols-[140px_1fr] gap-2">
                            <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Name:</span>
                            <span className="text-neutral-900 dark:text-neutral-100 text-sm">{orderUserObj.first_name}{orderUserObj.last_name ? ` ${orderUserObj.last_name.charAt(0)}.` : ''}</span>
                          </div>
                        )
                      ) : (
                        <p className="text-neutral-500 dark:text-neutral-400 text-sm">No attendee information</p>
                      )}
                    </div>
                  </div>
                </div>

                <OrderCardPayment order={order} />

                {/* Order Status Card */}
                <div className="bg-white dark:bg-neutral-900 p-4 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                  <div className="flex items-center">
                    {order.status === 'refunded' ? (
                      <div className="bg-purple-100 dark:bg-purple-900/50 p-2 rounded-full mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-purple-600 dark:text-purple-500">
                          <polyline points="1 4 1 10 7 10"></polyline>
                          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                        </svg>
                      </div>
                    ) : order.status === 'cancelled' ? (
                      <div className="bg-red-100 dark:bg-red-900/50 p-2 rounded-full mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-red-600 dark:text-red-500">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="15" y1="9" x2="9" y2="15"></line>
                          <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                      </div>
                    ) : (
                      <div className="bg-green-100 dark:bg-green-900/50 p-2 rounded-full mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-green-600 dark:text-green-500">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      </div>
                    )}
                    <div>
                      <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 capitalize">
                        {order.status?.replace('_', ' ') || 'Unknown'}
                      </h3>
                      {(order.completed_at) && (
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          Completed @ {formatDate(order.completed_at!, 'h:mm:ss a')}
                        </p>
                      )}
                    </div>
                    <div className="ml-auto">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(order.status)}`}>
                        {order.status?.replace('_', ' ').split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Customer Review Card */}
                {orderReview && (
                  <div className="bg-white dark:bg-neutral-900 p-4 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                    <h3 className="flex items-center text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-3">
                      <Star className="h-5 w-5 mr-2 text-yellow-500" />
                      Customer Review
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        {orderReview.rating_type === 'scale' && orderReview.scale_rating != null ? (
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={cn(
                                  'h-5 w-5',
                                  star <= orderReview.scale_rating!
                                    ? 'text-yellow-400 fill-yellow-400'
                                    : 'text-neutral-300 dark:text-neutral-600'
                                )}
                              />
                            ))}
                            <span className="ml-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                              {orderReview.scale_rating}/5
                            </span>
                          </div>
                        ) : orderReview.rating_type === 'thumbs' ? (
                          <div className="flex items-center gap-2">
                            {orderReview.thumbs_up ? (
                              <div className="flex items-center gap-1.5 text-green-600 dark:text-green-500">
                                <ThumbsUp className="h-5 w-5 fill-green-600 dark:fill-green-500" />
                                <span className="text-sm font-medium">Positive</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 text-red-600 dark:text-red-500">
                                <ThumbsDown className="h-5 w-5 fill-red-600 dark:fill-red-500" />
                                <span className="text-sm font-medium">Negative</span>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                      {orderReview.comment && (
                        <p className="text-sm text-neutral-700 dark:text-neutral-300 italic">
                          &ldquo;{orderReview.comment}&rdquo;
                        </p>
                      )}
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {formatDate(orderReview.created_at, 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Refund Form */}
                {showRefundForm && order && (
                  <div className="bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-900/40 rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="flex items-center text-lg font-bold text-orange-900 dark:text-orange-200">
                        <RotateCcw className="h-5 w-5 mr-2 text-orange-600 dark:text-orange-400" />
                        Refund Order
                      </h3>
                      <button
                        type="button"
                        onClick={closeRefundForm}
                        className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200"
                        aria-label="Cancel refund"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {refundMode === 'items' ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-neutral-700 dark:text-neutral-300">
                            Select items to refund (full quantity per line):
                          </p>
                          <button
                            type="button"
                            onClick={selectAllItems}
                            className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            Select all
                          </button>
                        </div>

                        <div className="space-y-1.5">
                          {order.order_items?.map((item: OrderItem) => {
                            const vendorItem = typeof item.vendor_event_item === 'object' ? item.vendor_event_item : null
                            const itemPrice = item.price_at_ordertime ?? vendorItem?.price ?? 0
                            const itemTotal = item.total || (itemPrice * (item.qty || 1))
                            const itemName = vendorItem?.name || item.name || 'Unknown Item'
                            const isSelected = selectedItemIds.has(item.id)
                            const discount = lineDiscountOf(item)

                            return (
                              <label
                                key={item.id}
                                className={cn(
                                  'flex items-center justify-between gap-3 p-2.5 rounded-md cursor-pointer border transition-colors',
                                  isSelected
                                    ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-800'
                                    : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 hover:bg-orange-50 dark:hover:bg-orange-900/10'
                                )}
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleItemSelection(item.id)}
                                    className="h-4 w-4 rounded border-neutral-300 text-orange-600 focus:ring-orange-500"
                                  />
                                  <div className="min-w-0">
                                    <div className="font-medium text-neutral-900 dark:text-neutral-100 truncate">
                                      {itemName}
                                    </div>
                                    {discount ? (
                                      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-neutral-600 dark:text-neutral-400">
                                        <span>{item.qty || 1} × {formatDiscountTokens(discount.chargedPrice)} tokens</span>
                                        <LineDiscountTag item={item} discount={discount} />
                                      </div>
                                    ) : (
                                      <div className="text-xs text-neutral-600 dark:text-neutral-400">
                                        {item.qty || 1} × {Math.round(itemPrice)} tokens
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="font-semibold text-neutral-900 dark:text-neutral-100">
                                    {Math.round(itemTotal)} tokens
                                  </div>
                                  <div className="text-xs text-neutral-600 dark:text-neutral-400">
                                    ${(itemTotal * tokenValue).toFixed(2)}
                                  </div>
                                </div>
                              </label>
                            )
                          })}
                        </div>

                        {canRefundOrder && (
                          <button
                            type="button"
                            onClick={() => setRefundMode('amount')}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            Need to refund a partial quantity? Use custom amount &rarr;
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-neutral-700 dark:text-neutral-300">
                          Enter a custom token amount to refund:
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            max={orderTotal}
                            step="0.5"
                            value={customAmount}
                            onChange={(e) => setCustomAmount(e.target.value)}
                            placeholder="0"
                            className="w-32 px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <span className="text-sm text-neutral-600 dark:text-neutral-400">
                            tokens (max: {orderTotal})
                          </span>
                        </div>
                        {canRefundItems && (
                          <button
                            type="button"
                            onClick={() => setRefundMode('items')}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            &larr; Back to item selection
                          </button>
                        )}
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-3 mt-3 border-t border-orange-200 dark:border-orange-900/40">
                      <div>
                        <div className="text-sm text-neutral-700 dark:text-neutral-300">Refund total:</div>
                        <div className="font-semibold text-neutral-900 dark:text-neutral-100">
                          {Math.round(refundTotalTokens)} tokens (${(refundTotalTokens * tokenValue).toFixed(2)})
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={closeRefundForm}
                          disabled={refundIsPending}
                          className="px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleRefundSubmit}
                          disabled={!refundIsValid || refundIsPending}
                          className="px-4 py-2 text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {refundIsPending ? 'Processing…' : 'Confirm Refund'}
                        </button>
                      </div>
                    </div>

                    {refundError && (
                      <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
                        {refundError.message || 'Failed to process refund'}
                      </div>
                    )}
                  </div>
                )}

                {/* Order Items Card */}
                <div className="bg-white dark:bg-neutral-900 p-4 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                  <h3 className="flex items-center text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500">
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                    </svg>
                    Order Items
                  </h3>

                  {/* Receipt-style items */}
                  <div className="space-y-3">
                    {order.order_items && order.order_items.length > 0 ? (
                      order.order_items.map((item: OrderItem, index: number) => {
                        const vendorItem = typeof item.vendor_event_item === 'object' ? item.vendor_event_item : null
                        const itemPrice = item.price_at_ordertime ?? vendorItem?.price ?? 0
                        const itemTotal = item.total || (itemPrice * (item.qty || 1))
                        const itemDollars = itemTotal * tokenValue
                        const pricePerItemDollars = itemPrice * tokenValue
                        const discount = lineDiscountOf(item)

                        return (
                          <div key={index} className="border-b border-neutral-200 dark:border-neutral-800 pb-3">
                            {/* Item header with name and total */}
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">
                                  {vendorItem?.name || item.name || 'Unknown Item'}
                                </h4>
                                {vendorItem?.description && (
                                  <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                                    {vendorItem.description}
                                  </p>
                                )}
                              </div>
                              <div className="text-right ml-4">
                                <div className="font-semibold text-neutral-900 dark:text-neutral-100">
                                  {Math.round(itemTotal)} Tokens
                                </div>
                                <div className="text-sm text-neutral-600 dark:text-neutral-400">
                                  ${itemDollars.toFixed(2)}
                                </div>
                              </div>
                            </div>

                            {/* Item details line */}
                            <div className="flex justify-between items-center text-xs text-neutral-600 dark:text-neutral-400">
                              {discount ? (
                                <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
                                  <span>{item.qty || 1} × {formatDiscountTokens(discount.chargedPrice)} Tokens</span>
                                  <LineDiscountTag item={item} discount={discount} />
                                </span>
                              ) : (
                                <span>{item.qty || 1} × {Math.round(itemPrice)} Tokens</span>
                              )}
                              <span>${pricePerItemDollars.toFixed(2)} each</span>
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <p className="text-neutral-500 dark:text-neutral-400">No items in this order</p>
                    )}

                    {/* Receipt totals */}
                    <div className="mt-6 pt-4">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-neutral-600 dark:text-neutral-400">Total Items:</span>
                          <span className="text-neutral-900 dark:text-neutral-100">{totalQuantity}</span>
                        </div>

                        {discountTokens > 0 && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-neutral-600 dark:text-neutral-400">Discount:</span>
                            <div className="text-right">
                              <div className="text-emerald-700 dark:text-emerald-400">
                                −{formatDiscountTokens(discountTokens)} Tokens
                              </div>
                              <div className="text-xs text-neutral-600 dark:text-neutral-400">
                                −${(discountTokens * tokenValue).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flex justify-between items-center text-lg font-bold pt-2 border-t border-neutral-200 dark:border-neutral-800">
                          <span className="text-neutral-900 dark:text-neutral-100">Total:</span>
                          <div className="text-right">
                            <div className="text-neutral-900 dark:text-neutral-100">
                              {Math.round(order.total || 0)} Tokens
                            </div>
                            <div className="text-sm font-semibold text-neutral-600 dark:text-neutral-400">
                              ${((order.total || 0) * tokenValue).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <EmailReceiptModal
        isOpen={showEmailModal}
        title="Email Order Receipt"
        defaultEmail={orderUserObj?.email}
        onClose={() => setShowEmailModal(false)}
        onSend={handleEmailReceipt}
      />
      <PrinterSelectionModal
        isOpen={showPrinterModal}
        onClose={() => setShowPrinterModal(false)}
      />
    </div>
  )
}
